import React from 'react'
import { format } from 'date-fns'
import { Calendar, X } from 'lucide-react'
import {
  DayCalculation,
  LOSCategory,
  PAXCategory,
  DayPriceOverride,
  OTAChannel,
  BusinessModel,
} from '../types'
import PricingMatrix from './PricingMatrix'

interface DayDetailsProps {
  selectedDate?: Date
  calculation?: DayCalculation
  selectedChannel: string
  otaChannels: OTAChannel[]
  losCategories: LOSCategory[]
  paxCategories: PAXCategory[]
  priceOverrides: DayPriceOverride[]
  onPriceOverride: (
    date: string,
    price: number,
    losId?: string,
    paxId?: string,
  ) => void
  onChannelSelect: (channelId: string) => void
  onClose?: () => void
  basePrice: number
  businessModel: BusinessModel
}

const DayDetails: React.FC<DayDetailsProps> = ({
  selectedDate,
  calculation,
  selectedChannel,
  otaChannels,
  losCategories,
  paxCategories,
  priceOverrides,
  onPriceOverride,
  onChannelSelect,
  onClose,
  basePrice,
  businessModel,
}) => {
  if (!selectedDate) {
    return (
      <div className="h-full bg-white border-l border-gray-200 p-6 flex items-center justify-center">
        <div className="text-center text-gray-500">
          <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium mb-2">Select a Date</p>
          <p className="text-sm">
            Choose a date from the list to view detailed pricing breakdown
          </p>
        </div>
      </div>
    )
  }

  const dateStr = format(selectedDate, 'yyyy-MM-dd')
  const hasOverride = priceOverrides.some(
    (override) => override.date === dateStr,
  )

  return (
    <div className="h-full bg-white overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-2 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-sm font-medium text-gray-900 whitespace-nowrap overflow-x-auto pl-35 p-3 flex-1">
            <span>{format(selectedDate, 'MMMM d')}</span>
            <span className="text-gray-400">·</span>
            <span>{format(selectedDate, 'EEEE')}</span>
          </div>

          <div className="flex items-center justify-center flex-1">
            <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
              {otaChannels
                .filter((channel) => channel.enabled)
                .map((channel) => (
                  <button
                    key={channel.id}
                    onClick={() => onChannelSelect(channel.id)}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      selectedChannel === channel.id
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {channel.name}
                  </button>
                ))}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-1 justify-end">
            {hasOverride && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 rounded-lg text-xs">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                Override
              </span>
            )}

            {onClose && (
              <button
                onClick={onClose}
                className="p-1 rounded hover:bg-gray-100 transition-colors flex-shrink-0"
                title="Close"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Pricing Matrix */}
      <div className="px-4 py-2 pr-40 pb-6">
        {calculation ? (
          <div>
            <PricingMatrix
              calculation={calculation}
              selectedChannel={selectedChannel}
              otaChannels={otaChannels}
              losCategories={losCategories}
              paxCategories={paxCategories}
              compact={false}
              onPriceOverride={onPriceOverride}
              onChannelSelect={onChannelSelect}
              selectedDate={selectedDate}
              priceOverrides={priceOverrides}
              basePrice={basePrice}
              businessModel={businessModel}
            />
          </div>
        ) : (
          <div className="text-center text-gray-500 py-8">
            <p className="text-sm">No pricing data available for this date</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default DayDetails
